import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WorkshopDashboard.css';

function WorkshopDashboard() {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/workshops/dashboard');
      setStats(response.data.stats);
      setRecentBookings(response.data.recentBookings || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch workshop dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="workshop-dashboard">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workshop-dashboard">
        <div className="ws-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="workshop-dashboard">
      <div className="ws-page-header">
        <h1>Workshop Dashboard</h1>
        <button className="ws-btn-primary" onClick={fetchDashboard}>
          Refresh
        </button>
      </div>

      <div className="ws-stat-cards">
        <div className="ws-stat-card">
          <div className="ws-stat-label">Upcoming Sessions (30 Days)</div>
          <div className="ws-stat-value">{stats?.upcomingSessions ?? 0}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-label">Bookings Today</div>
          <div className="ws-stat-value">{stats?.bookingsToday ?? 0}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-label">This Week Utilization</div>
          <div className="ws-stat-value">{stats?.weekUtilization ?? 0}%</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-label">Available Slots Today</div>
          <div className="ws-stat-value">{stats?.availableSlotsToday ?? 0}</div>
        </div>
      </div>

      <div className="ws-quick-links">
        <button className="ws-quick-link" onClick={() => navigate('/workshops/calendar')}>
          Calendar
        </button>
        <button className="ws-quick-link" onClick={() => navigate('/workshops/bookings')}>
          Bookings
        </button>
        <button className="ws-quick-link" onClick={() => navigate('/workshops/sessions')}>
          Sessions
        </button>
        <button className="ws-quick-link" onClick={() => navigate('/workshops/reports')}>
          Reports
        </button>
      </div>

      <div className="ws-section">
        <h2>Recent Bookings</h2>
        {recentBookings.length === 0 ? (
          <div className="ws-empty">No recent bookings found</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Session</th>
                  <th>Attendees</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((booking) => (
                  <tr
                    key={booking.id}
                    className="ws-clickable-row"
                    onClick={() => navigate(`/workshops/sessions/${booking.session_id}`)}
                  >
                    <td>{formatDate(booking.session_date)}</td>
                    <td className="ws-text-bold">{booking.customer_name}</td>
                    <td>{booking.customer_email}</td>
                    <td>{booking.workshop_type} - {booking.location_name}</td>
                    <td>{booking.attendee_count}</td>
                    <td>
                      <span className={`ws-badge ws-badge-${booking.status}`}>
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopDashboard;
